import { CircleAlert } from "lucide-react";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import {
    isCustomError,
    isInvalidOrExpiredToken,
    isNetworkError,
    isPermissionDenied
} from "./errorCheck";
import { errorManager } from "./ErrorManager";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useLogout } from "@/frontend/user/useLogout";

type ErrorType = "auth" | "custom" | "permission" | "network" | "unknown";

export const GlobalErrorDisplay = () => {
    const errors = useSyncExternalStore(
        errorManager.addChangeListener,
        errorManager.getErrors
    );

    const hasRetryCallbacks = useSyncExternalStore(
        errorManager.addChangeListener,
        errorManager.hasRetryCallbacks
    );

    return errors.length > 0 ? (
        <ErrorDisplay errors={errors} hasRetryCallbacks={hasRetryCallbacks} />
    ) : null;
};

const ErrorDisplay = ({
    errors,
    hasRetryCallbacks
}: {
    errors: Error[];
    hasRetryCallbacks: boolean;
}) => {
    // auto retry error if page url changes
    const { pathname } = useLocation();
    const pathRef = useRef(pathname);
    useEffect(() => {
        if (pathname !== pathRef.current) {
            errorManager.retryErrors();
        }
    }, [pathname]);

    const logout = useLogout();
    const relogin = async () => {
        await logout();
        errorManager.retryErrors();
    };

    const authError = errors.some(isInvalidOrExpiredToken);
    const customError = errors.find(isCustomError);
    const permissionError = errors.some(isPermissionDenied);
    const networkError = errors.some(isNetworkError);
    const errorType: ErrorType = authError
        ? "auth"
        : customError !== undefined
          ? "custom"
          : permissionError
            ? "permission"
            : networkError
              ? "network"
              : "unknown";

    return (
        <AlertDialog
            open={true}
            onOpenChange={isOpen => {
                if (!isOpen) {
                    errorManager.clearErrors();
                }
            }}
        >
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        <CircleAlert className="mb-2 size-10" />
                        {errorType === "auth" && "Session Error"}
                        {errorType === "custom" &&
                            (customError?.message ?? "Application Error")}
                        {errorType === "permission" && "Permission Error"}
                        {errorType === "network" && "Network Error"}
                        {errorType === "unknown" && "Application Error"}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                        {errorType === "auth" && (
                            <>
                                Your login session has expired. You will need to
                                re-login in order to continue using the app.
                            </>
                        )}
                        {errorType === "custom" && (
                            <>
                                {customError?.description ??
                                    "We're sorry - an error has occurred."}
                            </>
                        )}
                        {errorType === "permission" && (
                            <>
                                A permission error has occurred. Please contact
                                support if this persists.
                            </>
                        )}
                        {errorType === "network" && (
                            <>
                                We&apos;re sorry - a network error has occurred.
                            </>
                        )}
                        {errorType === "unknown" && (
                            <>We&apos;re sorry - an error has occurred. </>
                        )}
                        {(errorType === "network" ||
                            errorType === "unknown") && (
                            <>
                                It is possible that this error is temporary and
                                is worth retrying.
                            </>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Hide this message</AlertDialogCancel>
                    {errorType === "auth" && (
                        <Button
                            onClick={() => {
                                void relogin();
                            }}
                        >
                            Re-login
                        </Button>
                    )}
                    {errorType !== "auth" && hasRetryCallbacks && (
                        <Button
                            onClick={() => {
                                errorManager.retryErrors();
                            }}
                        >
                            Retry
                        </Button>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
